import React, { useEffect, useState, useRef } from 'react';
import { Link, useParams } from 'react-router-dom';
import { motion, useInView } from 'framer-motion';
import API from '../../api';
import { useLang } from '../../LangContext';
import ProductCard from '../../components/bazaar/ProductCard';
import { addToCart, getCartKey } from './cartUtils';

const Reveal = ({ children, delay = 0 }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-50px" });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 20 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.6, delay }}
    >
      {children}
    </motion.div>
  );
};

const TibebPattern = () => (
  <svg width="100%" height="100%" style={{ position: 'absolute', top: 0, left: 0, zIndex: 0, opacity: 0.04, pointerEvents: 'none' }}>
    <pattern id="tibeb" x="0" y="0" width="120" height="120" patternUnits="userSpaceOnUse">
      <path d="M60 0 L120 60 L60 120 L0 60 Z" fill="none" stroke="currentColor" strokeWidth="0.5" />
      <circle cx="60" cy="60" r="15" fill="none" stroke="currentColor" strokeWidth="0.5" />
      <path d="M0 0 L120 120 M120 0 L0 120" stroke="currentColor" strokeWidth="0.2" opacity="0.5" />
    </pattern>
    <rect width="100%" height="100%" fill="url(#tibeb)" />
  </svg>
);

export default function SellerStorePage() {
  const { id } = useParams();
  const { t } = useLang();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [toast, setToast] = useState('');

  useEffect(() => {
    API.get('/bazaar/products', { params: { seller_id: id } })
      .then(r => {
        const data = Array.isArray(r.data) ? r.data : [];
        setProducts(data);
        if (data.length === 0) setNotFound(true);
      })
      .catch(() => { setProducts([]); setNotFound(true); })
      .finally(() => setLoading(false));
  }, [id]);

  const showToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(''), 2000);
  };

  const handleAddToCart = (product) => {
    const key = getCartKey(product.seller_id);
    // getCartKey returns null when there is no signed-in user profile. Say so
    // instead of silently dropping the click.
    if (!key) { showToast(t.spSignInToSave); return; }
    let cart = [];
    try { cart = JSON.parse(localStorage.getItem(key)) || []; } catch { cart = []; }
    if (!Array.isArray(cart)) cart = [];
    cart = addToCart(cart, product, 1);
    localStorage.setItem(key, JSON.stringify(cart));
    showToast(`❤️ ${product.name} ${t.pdWishlistToast}`);
  };

  if (loading) {
    return <div style={{ textAlign: 'center', padding: 100, color: 'var(--primary)', fontWeight: 700 }}>{t.loading}</div>;
  }

  const seller = products.length > 0 ? {
    name: products[0].seller_name,
    location: products[0].seller_location || '',
    description: products[0].seller_description || '',
  } : null;

  return (
    <div className="page" style={{ paddingTop: 100 }}>
      <div className="hero-gradient" style={{ height: '30vh' }} />
      <TibebPattern />

      <Reveal>
        <Link to="/bazaar" style={{ color: 'var(--primary)', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 8, marginBottom: 32 }}>{t.backToBazaar}</Link>
      </Reveal>

      {notFound || !seller ? (
        <Reveal>
          <div style={{ textAlign: 'center', padding: 60 }}>
            <div style={{ fontSize: 64, marginBottom: 24 }}>🏪</div>
            <h2 style={{ fontSize: 24, color: 'var(--text-muted)' }}>{t.spNoProducts}</h2>
          </div>
        </Reveal>
      ) : (
        <>
          <Reveal delay={0.1}>
            <div className="creative-card" style={{ display: 'flex', alignItems: 'flex-start', gap: 24, marginBottom: 48, flexWrap: 'wrap' }}>
              <div style={{ width: 80, height: 80, background: 'var(--primary)', color: '#fff', borderRadius: 20, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 36, fontWeight: 900, flexShrink: 0 }}>
                {seller.name?.charAt(0)}
              </div>
              <div style={{ flex: 1, minWidth: 200 }}>
                <h1 style={{ fontSize: 'clamp(22px, 3.5vw, 30px)', fontWeight: 900, marginBottom: 8, color: 'var(--secondary)' }}>{seller.name}</h1>
                {seller.location && <p style={{ fontSize: 16, color: 'var(--text-muted)', marginBottom: 12 }}>📍 {seller.location}</p>}
                <p style={{ color: 'var(--text-main)', lineHeight: 1.6, margin: 0 }}>{seller.description || t.spVerifiedSeller}</p>
              </div>
            </div>
          </Reveal>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 32 }}>
            {products.map((product, idx) => (
              <Reveal key={product.id} delay={0.1 + idx * 0.05}>
                <ProductCard product={product} onAddToCart={handleAddToCart} />
              </Reveal>
            ))}
          </div>
        </>
      )}

      {toast && (
        <motion.div initial={{ y: 100 }} animate={{ y: 0 }} style={{ position: 'fixed', bottom: 40, right: 40, background: 'rgba(15,23,42,0.95)', color: '#fff', padding: '16px 32px', borderRadius: 20, fontWeight: 800, zIndex: 9999 }}>
          {toast}
        </motion.div>
      )}
    </div>
  );
}
